import { useRef } from 'react';
import {
  payrollOfferCta,
  payrollOfferModal,
  payrollTermsHref,
  payrollTermsNote,
  payrollYcCta,
} from '~/content/payroll';
import { ModalShell } from '~/components/layout';
import { ButtonLink, Icon, ResponsiveText } from '~/components/ui';
import { useContent } from '~/lib/useLanguage';

/**
 * "Learn more" dialog for the payroll offer (Figma 1280:50375 / mobile
 * 1280:50444), in the shared QR-modal chrome (blur backdrop, rising card,
 * cursor-following close), which also handles Escape, scroll lock and focus.
 */
export function PayrollOfferModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { ui } = useContent();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const content = payrollOfferModal;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      labelledBy="payroll-offer-title"
      initialFocusRef={closeButtonRef}
      className="w-full max-w-[750px]">
      {/* Tall content: the card scrolls inside itself rather than the page. */}
      <div className="flex max-h-[80dvh] flex-col overflow-y-auto overscroll-contain rounded-[32px] bg-white px-6 pt-6 pb-8 sm:max-h-[90dvh] md:px-8 md:pt-8">
        <button
          ref={closeButtonRef}
          type="button"
          aria-label={ui.close}
          onClick={onClose}
          className="flex size-6 cursor-pointer items-center justify-center self-end">
          <Icon name="close" />
        </button>

        <div className="mt-10 border-b border-black-6 pb-8">
          <h2
            id="payroll-offer-title"
            className="max-w-[329px] text-h3 leading-[1.16] font-semibold tracking-[-0.704px] md:max-w-[511px] md:text-h2 md:tracking-[-0.88px]">
            <ResponsiveText text={content.title} mobileText={content.mobileTitle} />
          </h2>
        </div>
        <p className="mt-10 text-body-m leading-[1.32] text-black md:mt-6 md:max-w-[686px] md:text-body">
          <ResponsiveText text={content.body} mobileText={content.mobileBody} />
        </p>

        <h3 className="mt-10 py-2 text-body-m leading-[1.16] font-semibold text-grey-1000 md:text-body">
          {content.stepsTitle}
        </h3>
        <ul className="mt-[17px] flex flex-col gap-2 border-b border-black-6 pb-7">
          {content.steps.map((step) => (
            <li key={step.label} className="flex items-center gap-5">
              <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-black-6">
                <img src={step.icon} alt="" aria-hidden="true" className="size-5" />
              </span>
              <span className="py-2 text-body-m leading-[1.16] text-grey-1000 md:text-body">
                {step.label}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-10 text-body leading-[1.32] text-grey-1000">
          <span className="block font-semibold">{content.closingTitle}</span>
          {content.closingBody}
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <ButtonLink href={payrollYcCta.href} external={payrollYcCta.external}>
            {payrollYcCta.label}
          </ButtonLink>
          <ButtonLink
            href={payrollOfferCta.href}
            external={payrollOfferCta.external}
            variant="secondary">
            {payrollOfferCta.label}
          </ButtonLink>
        </div>
        <p className="mt-[30px] text-body-s leading-[1.32] tracking-[0.252px] text-grey-500">
          <a
            href={payrollTermsHref}
            className="underline underline-offset-2 transition-colors hover:text-ink">
            {payrollTermsNote}
          </a>
        </p>
      </div>
    </ModalShell>
  );
}
